import { Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  DrawStage,
  WarningModal,
  WarningModalProps
} from "@toorak/tc-common-fe-sdk";
import { Header } from "../../create-loan/home/Header";
import { setTabView } from "../../stores/SettlementAndPurchase/SettlementAndPurchase.reducer";
import { isOrigUser } from "../../utils/AccessManagement";
import { DrawDetails } from "./DrawDetails";
import { useDrawsStyles } from "./Draws.styles";
import { RootState } from "../../stores/rootReducer";
import { DrawRequestStore } from "../../stores/drawRequest/drawRequest.interface";
import {
  getFieldHistory,
  getDrawDetailsByDrawId
} from "../../stores/drawRequest/drawRequest.action";
import { isBulkDrawViewEnabled } from "./DrawDetailsConstants";
import { updateDrawExcelData } from "../../stores/drawRequest/drawRequest.reducer";

export const BulkDrawDetails = () => {
  const navigate = useNavigate();
  const classes = useDrawsStyles();
  const dispatch = useDispatch();
  const isOrig = isOrigUser();
  const { drawId } = useParams<any>() as any;
  const [extractedDraws, setExtractedDraws] = useState<any>([]);
  const [isBulkDraw, setIsBulkDraw] = useState(false);
  const [openWarning, setOpenWarning] = useState<WarningModalProps | null>(
    null
  );
  const { excelData, drawDetails } = useSelector<RootState, DrawRequestStore>(
    (state) => state.drawRequestStore,
    shallowEqual
  );
  const { stage } = drawDetails;
  const { draft, drawSubmitted, DrawInQueue } = DrawStage;
  const lastStageSyncRef = useRef<string>("");
  const lastExcelDataLenRef = useRef<number>(-1);
  useEffect(() => {
    if (isBulkDrawViewEnabled()) {
      setIsBulkDraw(true);
    }
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    const len = excelData?.length ?? 0;
    if (len > 0) {
      if (lastExcelDataLenRef.current === len) return;
      lastExcelDataLenRef.current = len;
      setExtractedDraws(excelData);
    } else {
      if (lastExcelDataLenRef.current === 0) return;
      lastExcelDataLenRef.current = 0;
      const excelUploadDataInSession =
        sessionStorage.getItem("drawExcelUpload");
      if (excelUploadDataInSession) {
        const excelUploadData = JSON.parse(excelUploadDataInSession);
        setExtractedDraws(excelUploadData);
      } else {
        setExtractedDraws([]);
      }
    }
    // eslint-disable-next-line
  }, [excelData?.length]);

  useEffect(() => {
    if (!drawId) return;
    dispatch(getDrawDetailsByDrawId(drawId, isOrig, undefined, true));
  }, [drawId, dispatch, isOrig]);

  useEffect(() => {
    if (!isBulkDrawViewEnabled()) return;
    const excelUploadDataInSession =
      sessionStorage.getItem("drawExcelUpload");
    if (!excelUploadDataInSession) return;
    const stageKey = `${drawId}|${stage}`;
    if (lastStageSyncRef.current === stageKey) return;
    lastStageSyncRef.current = stageKey;
    const excelUploadData = JSON.parse(excelUploadDataInSession);
    const drawIndex = excelUploadData.findIndex(
      (item: { drawId: string }) => item.drawId.toString() === drawId
    );
    if (drawIndex < 0) return;
    excelUploadData[drawIndex] = { ...excelUploadData[drawIndex], stage };
    sessionStorage.setItem(
      "drawExcelUpload",
      JSON.stringify(excelUploadData)
    );
    dispatch(updateDrawExcelData(excelUploadData));
  }, [stage, drawId, dispatch]);

  useEffect(() => {
    if (isBulkDrawViewEnabled()) {
      dispatch(getFieldHistory(drawId, isOrig));
    }
  }, [drawId, isOrig, dispatch]);

  const handleSelectedDrawChange = (index: number) => {
    const tempObj = JSON.parse(JSON.stringify(extractedDraws));
    const drawIndex = tempObj.findIndex((x: any) => x.selected === true);

    if (drawIndex !== index) {
      if (stage !== drawSubmitted) {
        setOpenWarning({
          isOpen: true,
          header: "Warning",
          handleOptional: () => {
            setOpenWarning(null);
          },
          body: "Are you sure you want to leave? Click on Save button to retain the progress.",
          primaryBtnName: "Okay",
          handleClose: () => {
            tempObj.forEach((element: any) => {
              element.selected = false;
            });
            tempObj[index].selected = true;
            setExtractedDraws(tempObj);
            sessionStorage.setItem("drawExcelUpload", JSON.stringify(tempObj));
            setOpenWarning(null);
            navigate(`/drawDetails/${tempObj[index].drawId}`);
          }
        });
      } else {
        tempObj.forEach((element: any) => {
          element.selected = false;
        });
        tempObj[index].selected = true;
        setExtractedDraws(tempObj);
        sessionStorage.setItem("drawExcelUpload", JSON.stringify(tempObj));
        navigate(`/drawDetails/${tempObj[index].drawId}`);
      }
    }
  };
  const getDrawCount = (type: string) => {
    let filteredDraw: any = [];
    filteredDraw = extractedDraws.filter((key: any) => key.stage === type);
    return filteredDraw.length;
  };
  return (
    <Grid item container xs={12} sm={12} md={12} xl={12}>
      <Grid item container xs={12} sm={12} md={12} xl={12}>
        <Header
          value={isOrig ? 8 : 3}
          setTabValue={setTabView}
          showTab
          showNotificationIcon={false}
        />
        <Grid item container xs={12} sm={12} md={12} xl={12}>
          {isBulkDraw ? (
            <Grid item container xs={12} sm={12} md={12} xl={12}>
              <Grid
                item
                container
                xs={12}
                sm={3}
                md={2}
                xl={2}
                className={classes.excelDraw}
              >
                <Grid
                  item
                  container
                  xs={12}
                  sm={12}
                  md={12}
                  xl={12}
                  className={classes.excelContainer}
                >
                  <div className={classes.countContainer}>
                    <p className={classes.drawTitle}>
                      <span className={classes.drawCount}>
                        {extractedDraws.length}
                      </span>
                      Draw Requests
                    </p>
                  </div>
                  <div className={classes.individualCount}>
                    <div className={classes.draftContainer}>
                      DRAFT ({getDrawCount(draft)})
                    </div>
                    <div className={classes.submittedContainer}>
                      DRAW SUBMITTED ({getDrawCount(drawSubmitted)})
                    </div>
                    <div className={classes.submittedContainer}>
                      DRAW IN QUEUE ({getDrawCount(DrawInQueue)})
                    </div>
                  </div>
                  {extractedDraws?.length > 0 &&
                    extractedDraws.map((item: any, index: number) => (
                      <div
                        key={item.drawId ?? index}
                        className={
                          !item.selected
                            ? classes.drawInfo
                            : [classes.drawInfo, classes.drawInfoSelected].join(
                                " "
                              )
                        }
                        onClick={(e) => handleSelectedDrawChange(index)}
                      >
                        <p>{item.drawId}</p>
                        <p>Primary Loan Id</p>
                        {item.selected && (
                          <div
                            className={classes.selected}
                            data-testid="selected-loan"
                          />
                        )}
                        <p>{item.primaryLoanId}</p>
                      </div>
                    ))}
                </Grid>
              </Grid>
              <Grid
                item
                container
                xs={12}
                sm={9}
                md={10}
                xl={10}
                className={classes.drawView}
              >
                <DrawDetails drawId={drawId} isBulkDraw={isBulkDraw} />
              </Grid>
            </Grid>
          ) : (
            <DrawDetails drawId={drawId} isBulkDraw={isBulkDraw} />
          )}
        </Grid>
      </Grid>
      {openWarning && (
        <WarningModal
          isOpen={openWarning.isOpen}
          handleClose={openWarning.handleClose}
          header={openWarning.header}
          handleOptional={openWarning.handleOptional}
          body={openWarning.body}
          primaryBtnName={openWarning.primaryBtnName}
        />
      )}
    </Grid>
  );
};
